/**
 * Reusable company-type detail page.
 * Used by PvtLimitedPage, LLPPage, OPCPage, PublicCoPage, NGOPage.
 */
import AppLayout from '../components/layout/AppLayout';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/constants';
import Button from '../components/common/Button';
import { Icon, IconTile } from '../components/common/Icon';

/* Map each company-type accent colour to the matching brand Button variant. */
const ACCENT_VARIANT = {
  '#145f5f': 'primary', // Pvt Ltd — teal
  '#3a4a66': 'blue',    // LLP / OPC — navy
  '#8a6a1f': 'orange',  // Public / Listed — amber
  '#4a6b3f': 'success', // Section 8 / NGO — sage
};

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 text-center" style={{ boxShadow: '0 2px 12px rgba(8,17,31,0.06)' }}>
      <div className={`text-[28px] font-bold ${color}`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>{value}</div>
      <div className="text-[11px] text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

function FilingRow({ form, title, deadline, penalty, status }) {
  const STATUS_STYLE = {
    filed:   { bg: 'bg-[#e8efe5]',  text: 'text-[#4a6b3f]' },
    pending: { bg: 'bg-[#f3ebd7]',  text: 'text-[#8a6a1f]' },
    overdue: { bg: 'bg-[#f2e9e6]',  text: 'text-[#9a564a]' },
    na:      { bg: 'bg-[#f2eee6]',  text: 'text-[#8a7d66]' },
  };
  const s = STATUS_STYLE[status] || STATUS_STYLE.na;
  return (
    <tr className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
      <td className="px-4 py-3 text-[12px] font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: '#145f5f' }}>{form}</td>
      <td className="px-4 py-3 text-[12.5px] text-gray-800">{title}</td>
      <td className="px-4 py-3 text-[12px] text-gray-600">{deadline}</td>
      <td className="px-4 py-3 text-[11.5px] font-semibold" style={{ color: '#9a564a' }}>{penalty}</td>
      <td className="px-4 py-3">
        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${s.bg} ${s.text}`}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </td>
    </tr>
  );
}

export default function CompanyTypePage({ config }) {
  const navigate = useNavigate();
  const accentVariant = ACCENT_VARIANT[config.accentColor] || 'primary';

  return (
    <AppLayout pageTitle={config.shortName}>
      {/* Hero */}
      <div
        className="rounded-2xl p-6 mb-6 border"
        style={{ background: config.heroBg, borderColor: config.heroAccent, boxShadow: '0 4px 24px rgba(8,17,31,0.08)' }}
      >
        <div className="flex items-start gap-4">
          <IconTile name={config.icon} size="xl" tone={config.accentColor} />
          <div className="flex-1">
            <div className="text-[22px] font-bold mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: config.titleColor }}>
              {config.name}
            </div>
            <div className="text-[12.5px] mb-3" style={{ color: config.subtitleColor }}>{config.subtitle}</div>
            <div className="flex flex-wrap gap-2">
              {config.tags.map((tag) => (
                <span key={tag} className="text-[10.5px] font-semibold px-2.5 py-1 rounded-full" style={{ background: config.tagBg, color: config.tagText }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button variant={accentVariant} size="sm" onClick={() => navigate(ROUTES.AGM_CALC)}>
              Calculate Deadlines
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.AGENT)}
            >
              Ask ComplyV AI
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {config.stats.map((s) => (
          <StatCard key={s.label} label={s.label} value={s.value} color={s.color} />
        ))}
      </div>

      <div className="grid grid-cols-2 gap-5">
        {/* Annual Filings Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ boxShadow: '0 2px 16px rgba(8,17,31,0.07)' }}>
          <div className="px-5 py-3.5 border-b border-gray-100">
            <span className="text-[16px] font-bold flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <Icon name="documents" size={17} style={{ color: config.accentColor }} />
              Annual Compliance Filings
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#f8f5ef]">
                  {['Form', 'Title', 'Deadline', 'Penalty', 'Status'].map((h) => (
                    <th key={h} className="text-left px-4 py-2 text-[10px] uppercase tracking-[1px] text-gray-500 font-bold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {config.filings.map((f) => (
                  <FilingRow key={f.form} {...f} />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {/* Key Requirements */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ boxShadow: '0 2px 16px rgba(8,17,31,0.07)' }}>
            <div className="px-5 py-3.5 border-b border-gray-100">
              <span className="text-[16px] font-bold flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                <Icon name="legal" size={17} style={{ color: config.accentColor }} />
                Key Requirements
              </span>
            </div>
            <div className="p-4 flex flex-col gap-2.5">
              {config.requirements.map((req) => (
                <div key={req.title} className="flex gap-3 items-start">
                  <IconTile name={req.icon} size="sm" tone={config.accentColor} />
                  <div>
                    <div className="text-[12px] font-bold text-gray-800">{req.title}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">{req.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Compliance Health */}
          {config.complianceHealth && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ boxShadow: '0 2px 16px rgba(8,17,31,0.07)' }}>
              <div className="px-5 py-3.5 border-b border-gray-100">
                <span className="text-[16px] font-bold flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                <Icon name="reporting" size={17} style={{ color: config.accentColor }} />
                Portfolio Health
              </span>
              </div>
              <div className="p-4 flex flex-col gap-3">
                {config.complianceHealth.map((item) => (
                  <div key={item.label}>
                    <div className="flex justify-between text-[12px] mb-1">
                      <span className="text-gray-600">{item.label}</span>
                      <span className="font-bold text-gray-700">{item.percent}%</span>
                    </div>
                    <div className="h-[6px] bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${item.percent}%`, background: item.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
